import React from 'react'
import { useChangePassword } from '../hooks/useChangePassword'
import { PASSWORD_REQUIREMENT_MESSAGE } from '@common/utils/password-validator'

interface ChangePasswordFormProps {
  onLoadingChange?: (isLoading: boolean) => void
}

function ChangePasswordForm({ onLoadingChange }: ChangePasswordFormProps) {
  const {
    initialPassword,
    setInitialPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    showInitialPassword,
    showNewPassword,
    showConfirmPassword,
    isLoading,
    initialPasswordError,
    newPasswordError,
    confirmPasswordError,
    setInitialPasswordError,
    setNewPasswordError,
    setConfirmPasswordError,
    handleSubmit,
    toggleInitialPasswordVisibility,
    toggleNewPasswordVisibility,
    toggleConfirmPasswordVisibility,
  } = useChangePassword()

  // isLoadingの変化を親に通知
  React.useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleInitialPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInitialPassword(e.target.value)
    if (initialPasswordError) setInitialPasswordError(null)
  }

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value)
    if (newPasswordError) setNewPasswordError(null)
  }

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
    if (confirmPasswordError) setConfirmPasswordError(null)
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className={`form-group ${initialPasswordError ? 'has-error' : ''}`}>
        <label htmlFor="initialPassword">初回ログインパスワード</label>
        <div className="password-input-wrapper">
          <input
            id="initialPassword"
            type={showInitialPassword ? 'text' : 'password'}
            value={initialPassword}
            onChange={handleInitialPasswordChange}
            placeholder="初回ログインパスワード"
            autoComplete="current-password"
            className={initialPasswordError ? 'error' : ''}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={toggleInitialPasswordVisibility}
            aria-label={showInitialPassword ? 'パスワードを隠す' : 'パスワードを表示'}
          >
            <i className={showInitialPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
          </button>
        </div>
        {initialPasswordError && (
          <div className="field-error">
            <i className="bi bi-exclamation-circle"></i>
            <span>{initialPasswordError}</span>
          </div>
        )}
      </div>

      <div className={`form-group ${newPasswordError ? 'has-error' : ''}`}>
        <label htmlFor="newPassword">新しいパスワード</label>
        <div className="password-input-wrapper">
          <input
            id="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={handleNewPasswordChange}
            placeholder="新しいパスワード"
            autoComplete="new-password"
            className={newPasswordError ? 'error' : ''}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={toggleNewPasswordVisibility}
            aria-label={showNewPassword ? 'パスワードを隠す' : 'パスワードを表示'}
          >
            <i className={showNewPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
          </button>
        </div>
        {newPasswordError && (
          <div className="field-error">
            <i className="bi bi-exclamation-circle"></i>
            <span>{newPasswordError}</span>
          </div>
        )}
        <p className="password-requirement-text">
          {PASSWORD_REQUIREMENT_MESSAGE}
        </p>
      </div>

      <div className={`form-group ${confirmPasswordError ? 'has-error' : ''}`}>
        <label htmlFor="confirmPassword">新しいパスワード（再入力）</label>
        <div className="password-input-wrapper">
          <input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            placeholder="新しいパスワード（再入力）"
            autoComplete="new-password"
            className={confirmPasswordError ? 'error' : ''}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={toggleConfirmPasswordVisibility}
            aria-label={showConfirmPassword ? 'パスワードを隠す' : 'パスワードを表示'}
          >
            <i className={showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
          </button>
        </div>
        {confirmPasswordError && (
          <div className="field-error">
            <i className="bi bi-exclamation-circle"></i>
            <span>{confirmPasswordError}</span>
          </div>
        )}
      </div>


      <button type="submit" className="login-button" disabled={isLoading}>
        {isLoading ? '更新中...' : 'パスワードを更新'}
      </button>
    </form>
  )
}

export default ChangePasswordForm
